import type { Analysis, TechnicalSkill } from '../../types/models';

const DEBUG = process.env.NODE_ENV === 'development';

function validateOpenAIResponse(content: string) {
  const expectedSections = [
    'FIT_SCORE',
    'VERDICT', 
    'REASONING',
    'TECHNICAL_SKILLS',
    'RISK_FACTORS',
    'GROWTH_POTENTIAL'
  ];

  // Check for interview plan format - either old format or new multi-perspective format
  const hasOldInterviewFormat = content.includes('INTERVIEW_PLAN:');
  const hasNewPerspectiveFormat = content.includes('===HR_PERSPECTIVE===') || 
                                  content.includes('===MANAGER_PERSPECTIVE===');

  const foundSections = expectedSections.map(section => {
    const found = content.includes(section + ':');
    if (DEBUG && !found) {
      console.warn(`Missing section: ${section}`);
    }
    return { section, found };
  });

  // Add interview plan validation
  foundSections.push({ 
    section: 'INTERVIEW_PLAN', 
    found: hasOldInterviewFormat || hasNewPerspectiveFormat 
  });

  if (DEBUG) {
    console.log('Raw OpenAI Response:', content);
    console.log('Section validation:', foundSections);
    console.log('Has old interview format:', hasOldInterviewFormat);
    console.log('Has new perspective format:', hasNewPerspectiveFormat);
  }

  const missingSection = foundSections.find(s => !s.found);
  if (missingSection) {
    throw new Error(`Invalid response format from OpenAI: Missing ${missingSection.section} section`);
  }

  // Enhanced validation with more flexible patterns
  const validations = [
    {
      check: /FIT_SCORE:\s*(\d+(?:\.\d+)?)/i,
      message: 'FIT_SCORE must be a number'
    },
    {
      check: /VERDICT:\s*(STRONG_FIT|POTENTIAL_FIT|NEEDS_CONSIDERATION|NOT_RECOMMENDED)/i,
      message: 'VERDICT must be one of: STRONG_FIT, POTENTIAL_FIT, NEEDS_CONSIDERATION, NOT_RECOMMENDED'
    },
    {
      check: /REASONING:[\s\S]*?(-\s+.+(\n|$)|•\s+.+(\n|$)|\d+\.\s+.+(\n|$))+/,
      message: 'REASONING must contain bullet points (-, •, or numbered)'
    }
  ];

  validations.forEach(({ check, message }) => {
    if (!check.test(content)) {
      if (DEBUG) {
        console.warn(`Validation failed: ${message}`);
        console.warn('Content snippet:', content.substring(0, 200) + '...');
      }
      throw new Error(`Invalid response format from OpenAI: ${message}`);
    }
  });

  return true;
}

// Enhanced risk factors parser with improved severity detection
const parseRiskFactors = (text: string) => {
  if (!text) return [];

  if (DEBUG) {
    console.log('Parsing risk factors from:', text.substring(0, 200) + '...');
  }

  // Try multiple parsing strategies
  let factors: any[] = [];

  // Strategy 1: Enhanced structured format parsing
  const structuredFactors = text
    .split(/(?=(?:HIGH|MEDIUM|LOW)\s*SEVERITY|Severity:\s*(?:HIGH|MEDIUM|LOW)|-\s*Severity:\s*(?:HIGH|MEDIUM|LOW))/i)
    .filter(Boolean)
    .map(factor => {
      // Enhanced severity patterns to catch more variations
      const severityPatterns = [
        /(?:HIGH|MEDIUM|LOW)\s*SEVERITY/i,
        /Severity:\s*\[?(HIGH|MEDIUM|LOW)\]?/i,
        /-\s*Severity:\s*\[?(HIGH|MEDIUM|LOW)\]?/i,
        /^\s*-?\s*(HIGH|MEDIUM|LOW)(?:\s*SEVERITY)?/i,
        /\b(HIGH|MEDIUM|LOW)\b.*?(?:SEVERITY|Description|Mitigation)/i
      ];
      
      // Enhanced description patterns
      const descriptionPatterns = [
        /\*\s*Description:\s*\[?([^\n\]]+?)\]?(?:\s*\*\s*Mitigation:|\s*$)/i,
        /Description:\s*\[?([^\n\]]+?)\]?(?:\s*Mitigation:|\s*$)/i,
        /(?:HIGH|MEDIUM|LOW).*?(?:SEVERITY.*?)?\s*([^*\n]+?)(?:\s*\*\s*Mitigation:|\s*Mitigation:|\s*$)/i,
        /-\s*([^:*\n]+?)(?:\s*\*\s*Mitigation:|\s*Mitigation:|\s*$)/i
      ];
      
      // Enhanced mitigation patterns
      const mitigationPatterns = [
        /\*\s*Mitigation:\s*\[?([^\n\]]+?)\]?(?:\s*$|\s*(?:HIGH|MEDIUM|LOW))/i,
        /Mitigation:\s*\[?([^\n\]]+?)\]?(?:\s*$|\s*(?:HIGH|MEDIUM|LOW))/i,
        /Mit:\s*\[?([^\n\]]+?)\]?(?:\s*$|\s*(?:HIGH|MEDIUM|LOW))/i
      ];

      let severityMatch, descriptionMatch, mitigationMatch;

      // Try each pattern until one matches
      for (const pattern of severityPatterns) {
        severityMatch = factor.match(pattern);
        if (severityMatch) {
          if (DEBUG) {
            console.log('Found severity:', severityMatch[1] || severityMatch[0]);
          }
          break;
        }
      }

      for (const pattern of descriptionPatterns) {
        descriptionMatch = factor.match(pattern);
        if (descriptionMatch) {
          if (DEBUG) {
            console.log('Found description:', descriptionMatch[1]);
          }
          break;
        }
      }

      for (const pattern of mitigationPatterns) {
        mitigationMatch = factor.match(pattern);
        if (mitigationMatch) {
          if (DEBUG) {
            console.log('Found mitigation:', mitigationMatch[1]);
          }
          break;
        }
      }

      if (severityMatch && descriptionMatch) {
        const severity = (severityMatch[1] || severityMatch[0]).toUpperCase();
        const validSeverity = ['HIGH', 'MEDIUM', 'LOW'].includes(severity) ? severity : 'MEDIUM';
        
        return {
          severity: validSeverity as 'HIGH' | 'MEDIUM' | 'LOW',
          description: descriptionMatch[1].trim(),
          mitigation: mitigationMatch?.[1]?.trim()
        };
      }
      return null;
    })
    .filter((f): f is NonNullable<typeof f> => f !== null);

  if (structuredFactors.length > 0) {
    factors = structuredFactors;
  } else {
    // Strategy 2: Enhanced bullet point parsing with improved severity inference
    const bulletPoints = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.match(/^[-•*]\s+/) && line.length > 15)
      .map(line => line.replace(/^[-•*]\s+/, '').trim());

    factors = bulletPoints.map(point => {
      // Enhanced severity inference with more keywords
      const highRiskKeywords = /\b(critical|severe|major|high|dangerous|fatal|blocking|showstopper|significant|serious|urgent|immediate|crucial|essential)\b/i;
      const lowRiskKeywords = /\b(minor|low|small|slight|negligible|trivial|basic|simple|gradual|optional|nice-to-have)\b/i;
      
      let severity: 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM';
      if (highRiskKeywords.test(point)) severity = 'HIGH';
      else if (lowRiskKeywords.test(point)) severity = 'LOW';

      return {
        severity,
        description: point,
        mitigation: undefined
      };
    });
  }

  if (DEBUG) {
    console.log('Parsed risk factors:', factors);
  }

  return factors;
};

// Parse multi-perspective interview plans
function parseInterviewPerspectives(interviewPlanText: string): Record<string, string> {
  if (!interviewPlanText) return {};

  if (DEBUG) {
    console.log('Parsing multi-perspective interview plan from:', interviewPlanText.substring(0, 300) + '...');
  }
  
  // Always log perspective parsing for debugging
  console.log('🔍 Parsing interview perspectives from text length:', interviewPlanText.length);

  const perspectives: Record<string, string> = {};
  
  // Define perspective patterns with flexible matching
  const perspectivePatterns = [
    { key: 'hr', pattern: /===HR_PERSPECTIVE===([\s\S]*?)(?====\w+_PERSPECTIVE===|$)/i },
    { key: 'manager', pattern: /===MANAGER_PERSPECTIVE===([\s\S]*?)(?====\w+_PERSPECTIVE===|$)/i },
    { key: 'senior-manager', pattern: /===SENIOR_MANAGER_PERSPECTIVE===([\s\S]*?)(?====\w+_PERSPECTIVE===|$)/i },
    { key: 'c-level', pattern: /===C_LEVEL_PERSPECTIVE===([\s\S]*?)(?====\w+_PERSPECTIVE===|$)/i },
    { key: 'technical-lead', pattern: /===TECHNICAL_LEAD_PERSPECTIVE===([\s\S]*?)(?====\w+_PERSPECTIVE===|$)/i }
  ];

  perspectivePatterns.forEach(({ key, pattern }) => {
    const match = interviewPlanText.match(pattern);
    if (match && match[1]) {
      const perspectiveContent = match[1].trim();
      if (perspectiveContent.length > 50) { // Ensure substantial content
        perspectives[key] = perspectiveContent;
        if (DEBUG) {
          console.log(`Found ${key} perspective:`, perspectiveContent.substring(0, 100) + '...');
        }
      }
    }
  });

  // Fallback: If no perspectives found, treat entire content as manager perspective
  if (Object.keys(perspectives).length === 0 && interviewPlanText.trim().length > 50) {
    if (DEBUG) {
      console.log('No perspective markers found, treating as manager perspective');
    }
    perspectives.manager = interviewPlanText.trim();
  }

  // Always log parsed perspectives for debugging
  console.log('✅ Parsed perspectives:', Object.keys(perspectives));
  console.log('📊 Perspective content lengths:', Object.fromEntries(
    Object.entries(perspectives).map(([key, value]) => [key, value.length])
  ));

  if (DEBUG) {
    console.log('Parsed perspectives:', Object.keys(perspectives));
  }

  return perspectives;
}

// Enhanced technical skills parser with improved consistency
function parseTechnicalSkills(content: string): TechnicalSkill[] {
  if (!content) return [];

  console.log('Parsing technical skills from content:', content.substring(0, 200) + '...');

  const skills: TechnicalSkill[] = [];
  
  // Clean and normalize the content
  const cleanContent = content.trim().replace(/\r\n/g, '\n');
  
  // Strategy 1: Parse the expected structured format
  // Looking for patterns like:
  // - Skill: JavaScript
  //   * Proficiency: ADVANCED
  //   * Evidence: ...
  //   * Currency: ...
  
  const skillBlocks = cleanContent.split(/(?=^- Skill:|^Skill:)/gm).filter(block => block.trim().length > 0);
  
  for (const block of skillBlocks) {
    const skillMatch = block.match(/(?:^- )?Skill:\s*(.+?)(?:\n|$)/m);
    const proficiencyMatch = block.match(/\*?\s*Proficiency:\s*(.+?)(?:\n|$)/m);
    const evidenceMatch = block.match(/\*?\s*Evidence:\s*(.+?)(?:\n|\*|$)/m);
    const currencyMatch = block.match(/\*?\s*Currency:\s*(.+?)(?:\n|$)/m);
    
    if (skillMatch) {
      const skillName = skillMatch[1].trim().replace(/[\[\]]/g, '');
      const proficiency = proficiencyMatch?.[1]?.trim().replace(/[\[\]]/g, '').toUpperCase() || 'INTERMEDIATE';
      const evidence = evidenceMatch?.[1]?.trim().replace(/[\[\]]/g, '') || 'Not specified';
      const currency = currencyMatch?.[1]?.trim().replace(/[\[\]]/g, '') || 'Recent';
      
      // Validate proficiency level
      const validProficiencies: TechnicalSkill['proficiency'][] = ['EXPERT', 'ADVANCED', 'INTERMEDIATE', 'BASIC', 'BEGINNER'];
      const normalizedProficiency = validProficiencies.includes(proficiency as TechnicalSkill['proficiency']) 
        ? proficiency as TechnicalSkill['proficiency']
        : 'INTERMEDIATE';
      
      if (normalizedProficiency !== proficiency) {
        console.warn(`Invalid proficiency "${proficiency}" for skill "${skillName}", defaulting to INTERMEDIATE`);
      }
      
      skills.push({
        skill: skillName,
        proficiency: normalizedProficiency,
        evidence: evidence,
        currency: currency
      });
    }
  }
  
  // Strategy 2: Fallback to bullet point parsing only if Strategy 1 found nothing
  if (skills.length === 0) {
    console.log('No structured skills found, trying fallback parsing...');
    
    const bulletLines = cleanContent
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.match(/^[-•*]\s+/))
      .map(line => line.replace(/^[-•*]\s+/, '').trim());

    for (const line of bulletLines) {
      // Try to extract skill and proficiency from bullet points
      const skillProficiencyPatterns = [
        /^(.+?):\s*(EXPERT|ADVANCED|INTERMEDIATE|BASIC|BEGINNER)/i,
        /^(.+?)\s*\((EXPERT|ADVANCED|INTERMEDIATE|BASIC|BEGINNER)\)/i,
        /^(.+?)\s*-\s*(EXPERT|ADVANCED|INTERMEDIATE|BASIC|BEGINNER)/i
      ];
      
      let parsed = false;
      for (const pattern of skillProficiencyPatterns) {
        const match = line.match(pattern);
        if (match) {
          const [, skillName, proficiencyStr] = match;
          skills.push({
            skill: skillName.trim(),
            proficiency: proficiencyStr.toUpperCase() as TechnicalSkill['proficiency'],
            evidence: line,
            currency: 'Recent'
          });
          parsed = true;
          break;
        }
      }
      
      // If no proficiency found, extract just the skill name
      if (!parsed && line.length > 2) {
        const skillName = line.replace(/[:\-\(\)]/g, ' ').trim();
        if (skillName.length > 1) {
          skills.push({
            skill: skillName,
            proficiency: 'INTERMEDIATE',
            evidence: line,
            currency: 'Recent'
          });
        }
      }
    }
  }
  
  // Strategy 3: Last resort - try to extract any skill-like words if still nothing
  if (skills.length === 0) {
    console.log('No skills found in fallback, attempting skill extraction...');
    
    // Common technical terms that might be skills
    const skillKeywords = [
      'javascript', 'typescript', 'python', 'java', 'react', 'angular', 'vue', 'node',
      'express', 'mongodb', 'postgresql', 'mysql', 'aws', 'azure', 'docker', 'kubernetes',
      'git', 'css', 'html', 'sql', 'nosql', 'redis', 'elasticsearch', 'graphql', 'rest',
      'agile', 'scrum', 'ci/cd', 'jenkins', 'terraform', 'ansible'
    ];
    
    const foundSkills = new Set<string>();
    const contentLower = cleanContent.toLowerCase();
    
    skillKeywords.forEach(keyword => {
      if (contentLower.includes(keyword)) {
        foundSkills.add(keyword.charAt(0).toUpperCase() + keyword.slice(1));
      }
    });
    
    foundSkills.forEach(skill => {
      skills.push({
        skill: skill,
        proficiency: 'INTERMEDIATE',
        evidence: 'Mentioned in analysis',
        currency: 'Recent'
      });
    });
  }
  
  // Remove duplicates and validate
  const uniqueSkills = skills.filter((skill, index, self) => 
    index === self.findIndex(s => s.skill.toLowerCase() === skill.skill.toLowerCase())
  );
  
  console.log(`Parsed ${uniqueSkills.length} technical skills:`, uniqueSkills.map(s => `${s.skill} (${s.proficiency})`));
  
  return uniqueSkills.slice(0, 15); // Limit to reasonable number
}

// Enhanced growth potential parser
const parseGrowthPotential = (text: string) => {
  if (!text) return { areas: [], recommendations: [] };

  const parseList = (text: string) => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.match(/^[-•*\d+\.]\s+/))
      .map(line => line.replace(/^[-•*\d+\.]\s+/, '').trim())
      .filter(line => line.length > 0);
  };

  // Enhanced parsing with multiple strategies
  let areas: string[] = [];
  let recommendations: string[] = [];

  // Strategy 1: Standard structured format
  const areasMatch = text.match(/Areas?\s*:([\s\S]*?)(?=Recommendations?:|$)/i);
  const recommendationsMatch = text.match(/Recommendations?\s*:([\s\S]*?)(?=Areas?:|$)/i);

  if (areasMatch) {
    areas = parseList(areasMatch[1]);
  }

  if (recommendationsMatch) {
    recommendations = parseList(recommendationsMatch[1]);
  }

  // Strategy 2: If no clear sections, try to infer from content
  if (areas.length === 0 && recommendations.length === 0) {
    const allItems = parseList(text);
    
    // Split based on keywords
    allItems.forEach(item => {
      const isRecommendation = /\b(should|recommend|suggest|consider|develop|improve|enhance|learn|train|focus)\b/i.test(item);
      const isArea = /\b(area|potential|growth|strength|skill|capability|competency)\b/i.test(item);
      
      if (isRecommendation) {
        recommendations.push(item);
      } else if (isArea) {
        areas.push(item);
      } else {
        // Default to areas if unclear
        areas.push(item);
      }
    });
  }

  return { areas, recommendations };
};

// Enhanced reasoning parser
const parseReasoning = (text: string): string[] => {
  if (!text) return [];

  const patterns = [
    // Standard bullet points
    /^[-•*]\s+(.+)$/gm,
    // Numbered lists
    /^\d+\.\s+(.+)$/gm,
    // Dash variations
    /^[-–—]\s+(.+)$/gm,
    // Bullet variations
    /^[•●▪▫■□]\s+(.+)$/gm
  ];

  for (const pattern of patterns) {
    const matches = Array.from(text.matchAll(pattern));
    if (matches.length > 0) {
      return matches.map(match => match[1].trim()).filter(item => item.length > 0);
    }
  }

  // Fallback: split by lines and filter meaningful content
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 10 && !line.match(/^(REASONING|reasoning):?$/i))
    .slice(0, 10); // Limit to reasonable number
};

// Function to detect and remove duplicate questions
function deduplicateInterviewPlan(interviewPlan: string): string {
  if (!interviewPlan) return '';

  const seenQuestions = new Set<string>();
  
  // Split the plan into numbered sections
  const sections = interviewPlan.split(/(?=\d+\.\s*Question Category:)/);
  const uniqueSections: string[] = [];
  
  sections.forEach(section => {
    if (!section.trim()) return;
    
    const questionMatch = section.match(/Primary Question:\s*(.*?)(?=\s*Expected Answer:|\s*Follow-up Questions:|\s*Red Flags:)/i);
    if (questionMatch) {
      const question = questionMatch[1].trim();
      const normalizedQuestion = question.toLowerCase()
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      
      // Check for similarity with existing questions
      let isDuplicate = false;
      for (const seenNormalized of seenQuestions) {
        const similarity = calculateStringSimilarity(normalizedQuestion, seenNormalized);
        if (similarity > 0.7) { // 70% similarity threshold
          isDuplicate = true;
          if (DEBUG) {
            console.warn(`Duplicate question detected: "${question}" (similarity: ${similarity.toFixed(2)})`);
          }
          break;
        }
      }
      
      if (!isDuplicate) {
        seenQuestions.add(normalizedQuestion);
        uniqueSections.push(section);
      }
    } else {
      // If no primary question found, keep the section (might be intro text)
      uniqueSections.push(section);
    }
  });

  return uniqueSections.join('\n\n');
}

// Simple string similarity calculation (Jaccard similarity)
function calculateStringSimilarity(str1: string, str2: string): number {
  const words1 = new Set(str1.split(/\s+/).filter(word => word.length > 2));
  const words2 = new Set(str2.split(/\s+/).filter(word => word.length > 2));
  
  const intersection = new Set([...words1].filter(word => words2.has(word)));
  const union = new Set([...words1, ...words2]);
  
  return intersection.size / union.size;
}

export function parseAnalysisResponse(content: string): Omit<Analysis, 'id' | 'candidate_id' | 'job_id' | 'created_at'> {
  try {
    // Validate the response format first
    validateOpenAIResponse(content);

    // Enhanced REGEX patterns with more flexibility
    const fitScoreMatch = content.match(/FIT_SCORE:\s*(\d+(?:\.\d+)?)/i);
    const verdictMatch = content.match(/VERDICT:\s*(STRONG_FIT|POTENTIAL_FIT|NEEDS_CONSIDERATION|NOT_RECOMMENDED)/i);
    const reasoningMatch = content.match(/REASONING:([\s\S]*?)(?=TECHNICAL_SKILLS:|$)/i);
    const technicalSkillsMatch = content.match(/TECHNICAL_SKILLS:([\s\S]*?)(?=RISK_FACTORS:|$)/i);
    const riskFactorsMatch = content.match(/RISK_FACTORS:([\s\S]*?)(?=GROWTH_POTENTIAL:|===\w+_PERSPECTIVE===|$)/i);
    const growthPotentialMatch = content.match(/GROWTH_POTENTIAL:([\s\S]*?)(?=INTERVIEW_PLAN:|===\w+_PERSPECTIVE===|$)/i);
    // Fix: Capture everything after INTERVIEW_PLAN: including all perspectives
    const interviewPlanMatch = content.match(/INTERVIEW_PLAN:([\s\S]*$)/i) ||
                               content.match(/(===\w+_PERSPECTIVE===[\s\S]*$)/i); // Fallback for perspective-only format

    if (!fitScoreMatch || !verdictMatch || !reasoningMatch || !interviewPlanMatch) {
      const missingFields = [
        !fitScoreMatch && 'FIT_SCORE',
        !verdictMatch && 'VERDICT',
        !reasoningMatch && 'REASONING',
        !interviewPlanMatch && 'INTERVIEW_PLAN'
      ].filter(Boolean);

      if (DEBUG) {
        console.error('Failed to match required fields:', missingFields);
        console.error('Content:', content);
      }

      throw new Error(`Invalid response format from OpenAI: Missing ${missingFields.join(', ')}`);
    }

    // Parse score with decimal support
    const score = parseFloat(fitScoreMatch[1]);
    const clampedScore = Math.max(1, Math.min(10, score)); // Ensure 1-10 range

    // Process and deduplicate interview plan
    let interviewPlan = interviewPlanMatch[1].trim();
    
    // Debug: Log what we're about to parse
    console.log('🔍 Raw interview plan text length:', interviewPlan.length);
    console.log('🔍 Contains perspective markers:', interviewPlan.includes('===HR_PERSPECTIVE==='));
    console.log('🔍 Interview plan preview:', interviewPlan.substring(0, 200) + '...');
    
    // Parse interview perspectives - new functionality
    const interviewPerspectives = parseInterviewPerspectives(interviewPlan);
    
    // Debug: Log parsing results
    console.log('✅ Perspectives parsing completed. Found keys:', Object.keys(interviewPerspectives));
    console.log('📊 Perspective content sizes:', Object.fromEntries(
      Object.entries(interviewPerspectives).map(([key, value]) => [key, value.length])
    ));

    // If we have perspectives, use the manager perspective as the main interview_plan
    // This maintains backward compatibility while adding new functionality
    if (Object.keys(interviewPerspectives).length > 0 && interviewPerspectives.manager) {
      console.log('🔄 Using manager perspective as main interview plan');
      interviewPlan = interviewPerspectives.manager;
    } else if (Object.keys(interviewPerspectives).length > 0) {
      console.log('⚠️ No manager perspective found, using first available:', Object.keys(interviewPerspectives)[0]);
      interviewPlan = Object.values(interviewPerspectives)[0];
    }
    
    interviewPlan = deduplicateInterviewPlan(interviewPlan);

    return {
      fit_score: clampedScore,
      verdict: verdictMatch[1] as Analysis['verdict'],
      reasoning: parseReasoning(reasoningMatch[1]),
      technical_skills: parseTechnicalSkills(technicalSkillsMatch?.[1] || ''),
      risk_factors: parseRiskFactors(riskFactorsMatch?.[1] || ''),
      growth_potential: parseGrowthPotential(growthPotentialMatch?.[1] || ''),
      interview_plan: interviewPlan,
      interview_perspectives: interviewPerspectives, // New field
      cv_summary: '',
      job_requirements: '',
      questions_used: [],
      suggestions: [],
      status: 'pending'
    };
  } catch (error) {
    if (DEBUG) {
      console.error('Parser error:', error);
      console.error('Raw content:', content);
    }
    throw error;
  }
}

// Legacy support function
export function parseSkills(content: string): TechnicalSkill[] {
  return parseTechnicalSkills(content);
}
